import {
  SHBox,
  SHInputAdornment,
  SHNumericField,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { Dollar2SVG } from "@components/svgs";
import { DELAY_TIME } from "@constants";
import { AnalysisFeePortfolioDTO } from "@models/platform-analysis/entities/steps/fee";
import {
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useTheme,
} from "@mui/material";
import { MaxPortfolioSize } from "@pages/managed-accounts/_id/steps/portfolio-setup/config";
import { hexToRGBA, thousandSeparator } from "@utils";
import { isNil } from "lodash";
import { useMemo } from "react";
import { Controller, FieldPath, UseFormReturn } from "react-hook-form";
import { AnalysisFeeCalculatorStepConfigs as Configs } from "../../../../../configs";

const TooltipStyled = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    padding: "0px",
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
    {
      marginTop: "-1px",
    },
}));

type Props = {
  type: "weight" | "value";
  formMethods: UseFormReturn<AnalysisFeePortfolioDTO>;
  fieldPath: FieldPath<AnalysisFeePortfolioDTO>;
  disabled?: boolean;

  onValueChange?: (value: number) => void;
};

export const NumericInputController = ({
  type,
  formMethods,
  fieldPath,
  disabled,
  onValueChange,
}: Props) => {
  const { palette, shadows } = useTheme();

  const watchedValue = formMethods.watch(fieldPath) as number | null;

  const handleReset = (resetCase: "blur" | "focus") => {
    const value = formMethods.getValues(fieldPath);

    if (resetCase === "focus" && (Number(value) === 0 || value === null)) {
      formMethods.setValue(fieldPath, null);

      return;
    }

    if (value !== null) return;
    formMethods.setValue(fieldPath, type === "weight" ? 0 : "0");
  };

  const renderedValue = useMemo(() => {
    return isNil(watchedValue)
      ? watchedValue
      : parseFloat(
          Number(watchedValue || 0).toFixed(Configs.DefaultDecimalPlaces),
        );
  }, [watchedValue]);

  const tooltipTitle: JSX.Element | null = useMemo(() => {
    if (isNil(watchedValue)) return null;

    const isDifferent =
      type === "weight"
        ? String(renderedValue) !== String(watchedValue)
        : String(thousandSeparator(renderedValue ?? "")) !==
          String(thousandSeparator(watchedValue ?? ""));
    let renderTitle =
      watchedValue && isDifferent
        ? Number(watchedValue).toFixed(Configs.DefaultPrecision)
        : null;
    if (renderTitle && Number(renderTitle) === 0) {
      renderTitle = Configs.TooltipZeroOverflow;
    }

    return renderTitle ? (
      <SHStack
        sx={{
          padding: 1,
          marginTop: "5px",
          marginLeft: type === "value" ? "30px" : 0,
          backgroundColor: hexToRGBA(palette.common.white, 0.85),
          border: "1px solid #E3E3E3",
          backdropFilter: "blur(2px)",
          borderRadius: "3px",
          boxShadow: shadows[1],
          position: "relative",
          color: palette.text.third,
        }}
      >
        {renderTitle}
      </SHStack>
    ) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedValue, type, watchedValue]);

  return (
    <TooltipStyled
      placement="bottom-start"
      enterDelay={DELAY_TIME}
      enterNextDelay={DELAY_TIME}
      title={tooltipTitle}
    >
      <SHBox>
        <Controller
          name={fieldPath}
          control={formMethods.control}
          render={({ field: { onChange } }) => {
            return (
              <SHNumericField
                disabled={disabled}
                min={0}
                max={type === "weight" ? 100 : MaxPortfolioSize}
                value={renderedValue}
                onChange={(_, value) => {
                  const formValue = isNil(value)
                    ? value
                    : type === "weight"
                    ? value
                    : value.toString();
                  onChange(formValue);
                  onValueChange?.(value ?? 0);
                }}
                onFocus={() => handleReset("focus")}
                onBlur={() => handleReset("blur")}
                numericFormatProps={{
                  thousandSeparator: true,
                  decimalScale: 2,
                  allowNegative: false,
                }}
                InputProps={{
                  endAdornment:
                    type === "weight" ? (
                      <SHInputAdornment position="end">
                        <SHTypography
                          variant="subtitle1"
                          fontSize={"18px"}
                          fontWeight={900}
                          lineHeight={"160%"}
                          disabled
                        >
                          %
                        </SHTypography>
                      </SHInputAdornment>
                    ) : null,
                  startAdornment:
                    type === "value" ? (
                      <SHInputAdornment position="start">
                        <Dollar2SVG width={18} height={18} />
                      </SHInputAdornment>
                    ) : null,
                }}
                sx={{
                  "& .MuiInput-root": {
                    "&::before,&::after": {
                      right: type === "weight" ? "30px" : 0,
                      left: type === "value" ? "30px" : 0,
                    },
                    "& .MuiInput-input": {
                      fontSize: "14px",
                      lineHeight: 1.2,
                      fontWeight: 600,
                      textAlign: type === "weight" ? "right" : "left",
                      paddingBottom: "12px",
                    },
                  },
                }}
              />
            );
          }}
        />
      </SHBox>
    </TooltipStyled>
  );
};
